import React, { useEffect, useRef, useState } from 'react';

const getScrollProgress = (element) => {
  if (!element) {
    return { progress: 0, visible: false };
  }

  const maxScroll = element.scrollHeight - element.clientHeight;
  if (maxScroll <= 2) {
    return { progress: 0, visible: false };
  }

  const progress = Math.min(1, Math.max(0, element.scrollTop / maxScroll));
  return { progress, visible: true };
};

const ScrollProgressBar = ({ scrollRef, watchKey }) => {
  const frameRef = useRef(null);
  const [state, setState] = useState({ progress: 0, visible: false });

  useEffect(() => {
    const element = scrollRef?.current;
    if (!element) return undefined;

    const updateProgress = () => {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        setState(getScrollProgress(element));
      });
    };

    updateProgress();

    element.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateProgress);
      resizeObserver.observe(element);
      if (element.firstElementChild) {
        resizeObserver.observe(element.firstElementChild);
      }
    }

    return () => {
      element.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [scrollRef, watchKey]);

  return (
    <div
      className={`scroll-progress-bar ${state.visible ? 'scroll-progress-bar--visible' : ''}`}
      aria-hidden={!state.visible}
    >
      <div
        className="scroll-progress-bar__track"
        style={{ transform: `scaleX(${state.progress})` }}
      />
    </div>
  );
};

export default ScrollProgressBar;
